<template>
  <div
    class="select-standard"
    :class="classes"
    @click="isOpen = !isOpen"
  >
    <span class="select-standard__value">
      <span
        v-if="!hasValue"
        class="select-standard__placeholder"
      >{{ placeholder }}</span>
      <span
        v-else
        class="select-standard__selected"
      >{{ label }}</span>
    </span>
    <span class="select-standard__icon">
      <slot name="dropdown-icon">
        <q-icon name="mono-chevron-down" />
      </slot>
    </span>
    <Menu
      v-model="isOpen"
      anchor="bottom left"
      self="top left"
      no-parent-event
      fit
      auto-close
      separate-close-popup
    >
      <slot />
    </Menu>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { cx } from '@/helpers'
import Menu from '../Menu/Menu.vue'

defineOptions({ name: 'Select' })
const model = defineModel({ type: [String, Number, Object], default: null })
const props = defineProps({
  size: {
    type: String,
    default: 'md',
    validator: v => ['xs', 'sm', 'md', 'lg', 'xl'].includes(v)
  },
  shape: {
    type: String,
    default: 'rounded',
    validator: v => ['square', 'rounded'].includes(v)
  },
  placeholder: { type: String, default: null },
  displayValue: { type: String, default: null },
  isDisabled: { type: Boolean, default: false },
  isFullWidth: { type: Boolean, default: false }
})

const isOpen = ref(false)

const classes = computed(() => cx(
  `size-${props.size}`,
  `shape-${props.shape}`,
  isOpen.value && 'is-open',
  props.isDisabled && 'is-disabled',
  props.isFullWidth && 'is-full-width'
))

const hasValue = computed(() => model.value != null && model.value !== '')
const label = computed(() => props.displayValue ?? (hasValue.value ? String(model.value) : ''))
</script>

<style scoped>
@reference "#app-styles.css";

.select-standard {
  @apply inline-flex items-center border edge-base font-medium cursor-pointer select-none transition-colors;

  &:not(.is-disabled):hover { @apply surface-surf; }

  &.shape-square  { @apply rounded-md; }
  &.shape-rounded { @apply rounded-[28px]; }

  &.is-disabled   { @apply opacity-60 pointer-events-none; }
  &.is-full-width { @apply w-full; }

  &.size-xs {
    @apply text-[10px]! leading-3 min-h-5 px-2 py-0.75 gap-1;
    .select-standard__icon :deep(.q-icon) { @apply text-[12px] leading-3; }
  }
  &.size-sm {
    @apply text-[12px]! leading-3.5 min-h-6 px-2.25 py-1 gap-1.25;
    .select-standard__icon :deep(.q-icon) { @apply text-[14px] leading-3.5; }
  }
  &.size-md {
    @apply text-[14px]! leading-4 min-h-7 px-2.75 py-1.25 gap-1.75;
    .select-standard__icon :deep(.q-icon) { @apply text-[16px] leading-4; }
  }
  &.size-lg {
    @apply text-[16px] leading-4.5 min-h-8 px-3 py-1.5 gap-2;
    .select-standard__icon :deep(.q-icon) { @apply text-[18px]! leading-4.5; }
  }
  &.size-xl {
    @apply text-[18px] leading-4 min-h-9 px-3.5 py-1.75 gap-2.25;
    .select-standard__icon :deep(.q-icon) { @apply text-[20px] leading-4; }
  }

  .select-standard__value       { @apply flex-1 truncate; }
  .select-standard__placeholder { @apply ink-subtle; }
  .select-standard__selected    { @apply ink-soft; }

  .select-standard__icon {
    @apply shrink-0 ink-subtle transition-transform duration-200;
  }
  &.is-open .select-standard__icon { @apply rotate-180; }
}
</style>
